import React, { FC, useCallback, useContext, useMemo } from 'react';
import { AuthDialog } from '../components/AuthDialog/AuthDialog';
import { useHistory } from 'react-router-dom';
import { useApiErrorHandler } from '../hooks/useApiErrorHandler';
import { NotificationContext } from '../contexts/NotificationContext';
import { useTranslation } from 'react-i18next';
import { useQueryParam } from '../hooks/useQueryParam';
import { apiAuth } from '../resources/api/apiAuth';
import Button from '@mui/material/Button';
import { GuestRoutes } from '../routers/GuestRouter';
import { isChangeEmailTokenData, isSignupVerifyTokenData, parseAccessToken } from '../helpers/parseAccessToken';
import { NotFoundComponent } from '../components/NotFound';
import { AuthGoBack } from '../components/AuthDialog/AuthGoBack';
import { AuthContext } from '../contexts/AuthContext';

const { useVerifyEmailByToken } = apiAuth;

export const VerifyEmail: FC = () => {
  const { t } = useTranslation();
  const { addNotification } = useContext(NotificationContext);
  const { forgetIdentity } = useContext(AuthContext);

  const { execute } = useVerifyEmailByToken();

  const handleError = useApiErrorHandler();
  const history = useHistory();

  const code = useQueryParam('code');

  const decoded = useMemo(() => {
    if (!code) return null;

    try {
      return parseAccessToken(code);
    } catch (e) {
      return null;
    }
  }, [code]);

  const onSubmit = useCallback(async () => {
    try {
      if (!code) throw new Error('No code');
      await execute(code);

      if (decoded && isChangeEmailTokenData(decoded.data)) {
        forgetIdentity(decoded.email);
      }

      addNotification({
        severity: 'success',
        message: t('auth.verifyEmail.success'),
      });
      history.replace(GuestRoutes.Start());
    } catch (e) {
      handleError(e);
    }
  }, [code, decoded]);

  const buttons = useMemo(
    () => [
      <AuthGoBack key={'back'} to={GuestRoutes.Start()} />,
      <Button key={'submit'} variant={'contained'} onClick={onSubmit}>
        {t('auth.verifyEmail.confirm')}
      </Button>,
    ],
    [onSubmit]
  );

  const labels = useMemo(() => {
    if (decoded && isSignupVerifyTokenData(decoded.data)) {
      return {
        title: t('auth.verifyEmail.title'),
        subtitle: t('auth.verifyEmail.subtitleRegistration'),
      };
    } else if (decoded && isChangeEmailTokenData(decoded.data)) {
      return {
        title: t('auth.verifyEmail.title'),
        subtitle: t('auth.verifyEmail.subtitleChangeEmail'),
      };
    } else {
      return {
        title: t('auth.verifyEmail.title'),
        subtitle: t('auth.verifyEmail.subtitleOther'),
      };
    }
  }, [decoded]);

  if (!decoded) {
    return <NotFoundComponent />;
  }

  const email = isChangeEmailTokenData(decoded.data) ? decoded.data.new_email : decoded.email;

  return (
    <AuthDialog title={labels.title} subtitle={email} actions={buttons}>
      {labels.subtitle}
    </AuthDialog>
  );
};
